// eslint-disable-next-line no-restricted-imports
import icoWinInfo from './../ico-win-info.svg';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { AppContext, AppContextType } from 'context';
import { Orientation } from 'pages/KeyPlan/GestureWindow';
import { InfoCard } from 'pages/KeyPlan/components/infoCard';
import { useImageStatus } from 'pages/KeyPlan/hooks/useImageStatus';
import React, {
  Fragment,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { AvailabilityDataType, UnitDataType } from 'types';
import { Marker } from 'types/configuration';

type ModalResponsiveType = {
  currentMarker: Marker;
  availabilityData: AvailabilityDataType;
  showModal: boolean;
  setShowModal: React.Dispatch<SetStateAction<boolean>>;
  windowIsFlipped: boolean;
  setWindowIsFlipped: React.Dispatch<SetStateAction<boolean>>;
  currentMarkers?: Marker[];
};

export const ModalResponsive: React.FC<ModalResponsiveType> = ({
  currentMarker,
  availabilityData,
  showModal,
  setShowModal,
  setWindowIsFlipped,
  windowIsFlipped,
  currentMarkers,
}) => {
  const { useStyles } = useContext<AppContextType>(AppContext);
  const { t } = useTranslation();

  const classes = useStyles();

  const localClasses = useLocalStyles();

  // If the marker has a "label" property, we use it as the image name, while we keep the "title"
  // to retreive the property data from the availabilities.json file
  // If no "label" is specified, we use the "title" property as image name
  const propertyAndImageName = useMemo(
    () =>
      currentMarkers?.find((m) => m?.title === currentMarker.title)?.label ??
      currentMarker.title,
    [currentMarker.title, currentMarkers],
  );

  const { imageIsLoading, imageIsAvailable } =
    useImageStatus(propertyAndImageName);

  const unitData = useMemo<UnitDataType | undefined>(
    () => availabilityData.units.find((u) => u.name === currentMarker?.title),
    [availabilityData.units, currentMarker?.title],
  );

  const [
    opportunityProductDeletionInProgress,
    setOpportunityProductDeletionInProgress,
  ] = useState(false);

  return (
    <Modal
      className={localClasses.planimetryModal}
      show={showModal}
      onHide={() => {
        setShowModal(false);
      }}
      size={windowIsFlipped || imageIsAvailable ? 'xl' : 'sm'}
    >
      {imageIsLoading && <LoadingSpinner />}

      {!imageIsLoading && (
        <Fragment>
          <Modal.Header closeButton className={localClasses.modalHeader}>
            <Modal.Title>{currentMarker?.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body
            className="pt-1"
            style={{
              backgroundColor: '#f7f7f6',
              borderRadius: 7,
              fontSize: 13,
              fontFamily: 'Nunito sans',
            }}
          >
            <img
              src={icoWinInfo}
              alt="Close Info"
              className={localClasses.infoIcon}
              onClick={() => setWindowIsFlipped((prev) => !prev)}
            />
            {imageIsAvailable && !imageIsLoading && !windowIsFlipped && (
              <img
                src={`/image/planimetry/${propertyAndImageName}.png`}
                alt={currentMarker.title}
                className="img-fluid"
              />
            )}
            {!imageIsAvailable && !imageIsLoading && !windowIsFlipped && (
              <div className={classes.unavailableImage}>
                <h5>{t('unavailableImage').toLocaleUpperCase()}</h5>
              </div>
            )}

            {windowIsFlipped && (
              <div className="py-1 p-sm-2 px-lg-5">
                <InfoCard
                  propertyName={currentMarker.title}
                  unitData={unitData}
                  windowOrientation={Orientation.Portrait}
                  opportunityProductDeletionInProgress={
                    opportunityProductDeletionInProgress
                  }
                  setOpportunityProductDeletionInProgress={
                    setOpportunityProductDeletionInProgress
                  }
                  isFullScreen={false}
                />
              </div>
            )}
          </Modal.Body>
        </Fragment>
      )}
    </Modal>
  );
};

const useLocalStyles = createUseStyles({
  modalHeader: {
    '& .btn-close': {
      backgroundColor: 'white!important',
      borderRadius: '100%!important',
    },
    '& .modal-title': {
      color: '#ffffffa1',
    },
    paddingBottom: '0!important',
    border: 'none',
    height: 50,
  },
  infoIcon: {
    top: -34,
    right: 45,
    position: 'absolute',
    cursor: 'pointer',
    width: 32,
    height: 34,
    marginLeft: 20,
  },
  planimetryModal: {
    '& .modal-content': {
      background: 'none',
      border: 'none',
      position: 'relative',
    },
  },
});
